/* import __COLOCATED_TEMPLATE__ from './fin-phone-guidance.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type { GuidelineCategory } from 'embercom/models/ai-agent/guidelines';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
  };
}

export default class FinPhoneGuidance extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare appService: $TSFixMe;

  @tracked isLoading = true;
  @tracked guidelines: Guideline[] = [];

  @tracked activeGuidelineCategories: GuidelineCategory[] = ['voice_routing'];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.setCategories();
    this.loadGuidelines();
  }

  get title() {
    return this.intl.t(`operator.fin.setup.tabs.setup-and-go-live.phone.fin-guidance.title`);
  }

  get guidelinesCountLabel() {
    return this.intl.t(`operator.fin.setup.tabs.setup-and-go-live.phone.fin-guidance.count`, {
      count: this.voiceGuidelines.length,
    });
  }

  get voiceGuidelines(): Guideline[] {
    return this.store
      .peekAll('ai-agent/guidelines')
      .toArray()
      .filter(
        (guideline) =>
          guideline.enabled && this.activeGuidelineCategories.includes(guideline.category),
      );
  }

  get routingGuidelinesCount(): number {
    return this.voiceGuidelines.filter((guideline) => guideline.category === 'voice_routing')
      .length;
  }

  get clarificationGuidelinesCount(): number {
    return this.voiceGuidelines.filter((guideline) => guideline.category === 'voice_clarification')
      .length;
  }

  get toneGuidelinesCount(): number {
    return this.voiceGuidelines.filter((guideline) => guideline.category === 'voice_tone').length;
  }

  get guidancesSummary() {
    let results: { icon: InterfaceIconName; label: string; isLast: boolean; type: string }[] = [];

    if (this.activeGuidelineCategories.includes('voice_tone') && this.toneGuidelinesCount) {
      results.push({
        icon: 'microphone',
        type: 'voice_tone',
        label: this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.fin-guidance.communication-guidance',
          {
            count: this.toneGuidelinesCount,
          },
        ),
        isLast: false,
      });
    }

    if (this.clarificationGuidelinesCount) {
      results.push({
        icon: 'messenger-questions-filled',
        type: 'voice_clarification',
        label: this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.fin-guidance.context-guidance',
          {
            count: this.clarificationGuidelinesCount,
          },
        ),
        isLast: false,
      });
    }

    if (this.routingGuidelinesCount) {
      results.push({
        icon: 'new-direct-message',
        type: 'voice_routing',
        label: this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.fin-guidance.handover-escalation-guidance',
          {
            count: this.routingGuidelinesCount,
          },
        ),
        isLast: true,
      });
    }

    return results;
  }

  setCategories() {
    this.activeGuidelineCategories = ['voice_routing', 'voice_clarification', 'voice_tone'];
  }

  async loadGuidelines() {
    try {
      await this.store.findAll('ai-agent/guidelines');
    } catch (error) {
      console.error('Failed to load guidelines:', error);
    }

    this.isLoading = false;
  }

  @action
  navigateToGuidance() {
    let url = this.router.urlFor('apps.app.automation.fin-ai-agent.guidance', {
      queryParams: {
        tab: 'voice',
      },
    });
    safeWindowOpen(url, '_blank');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::FinPhoneGuidance': typeof FinPhoneGuidance;
  }
}
